import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { reportClientError } from "../utils/errorReporter";

/**
 * Sprint 74 — Root React error boundary.
 *
 * Wraps the app so any render-time crash becomes a friendly card instead
 * of a white screen, AND ships the stack trace to /api/client-errors so
 * the admin error-log surfaces it.
 *
 * Usage (in App.js):
 *   <RootErrorBoundary>
 *     <Routes>...</Routes>
 *   </RootErrorBoundary>
 */
export default class RootErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    reportClientError({
      message: error?.message || "React render error",
      stack: (error?.stack || "") + "\n\nComponent stack:\n" + (info?.componentStack || ""),
      source: "react_boundary",
      severity: "error",
    });
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen bg-slate-50 flex items-center justify-center p-4"
          data-testid="root-error-boundary"
        >
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 max-w-md w-full text-center">
            <div className="w-12 h-12 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-3">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Something went wrong</h2>
            <p className="text-sm text-slate-500 mt-1.5">
              The page hit an unexpected error. Our team was notified and we're on it.
            </p>
            {this.state.error?.message && (
              <pre className="mt-3 text-[11px] text-slate-400 bg-slate-50 rounded-lg p-2 overflow-x-auto text-left">
                {String(this.state.error.message).slice(0, 240)}
              </pre>
            )}
            <button
              onClick={this.reset}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium"
              data-testid="root-error-reload"
            >
              <RefreshCw className="h-4 w-4" /> Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
